import { IResourceInfo, IView, IViewEditable, IViewInfo } from "../../../view/IView";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { RoutesEditorLayoutConfig } from "./Layout";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditor } from "../../../IEditor";
import { RoutesModel } from "./Common/RoutesModel";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { createEventEmitter, emitEvent, onEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { ABILITY_ACTIONS, AbilityService } from "../../../ability/AbilityService";
import { IEditorResource_Routes } from "../../../resources/ResourceTypes";
import { RESOURCE_TYPES } from "@adapptio/lib-core";

interface IRoutesEditorParams {
	resourceId: string;
}

export class RoutesEditor implements IView<IRoutesEditorParams>, IViewEditable {
	private abilitySvc: AbilityService;
	private resourceSvc: ResourceService;
	private runtimeSvc: RuntimeService;
	private routesModel: RoutesModel;
	private resource: IEditorResource_Routes;

	private layout = new LayoutState(RoutesEditorLayoutConfig);
	private isModified = false;
	private saveInProgress = false;

	public onResourceInfoChanged = createEventEmitter();
	public onViewInfoChanged = createEventEmitter();
	public onHistoryChange = createEventEmitter();

	constructor(private dc: IEditor) {
		this.abilitySvc = dc.get("abilityService");
		this.resourceSvc = dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
	}

	public async init(params: IRoutesEditorParams, id: string): Promise<void> {
		const resourceBp = await this.resourceSvc.getBlueprintById(params.resourceId);

		if (resourceBp?.resourceType !== RESOURCE_TYPES.ROUTES) {
			throw new Error(
				`Resource should be ${RESOURCE_TYPES.ROUTES} but it's ${String(resourceBp?.resourceType)}`
			);
		}

		this.resource = resourceBp;

		this.routesModel = new RoutesModel(this.runtimeSvc);
		await this.routesModel.loadDocument(resourceBp.content);

		onEvent(this.routesModel.onModelChange, () => {
			this.setIsModified(true, true);
		});
	}

	private emitResourceInfoChange(): void {
		emitEvent(this.onResourceInfoChanged);
	}

	private emitViewInfoChange(): void {
		emitEvent(this.onViewInfoChanged);
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			this.emitViewInfoChange();
		}
	}

	public undo(): void {
		this.routesModel.undoRedoManager.undo();
		this.setIsModified(true, true);
	}

	public redo(): void {
		this.routesModel.undoRedoManager.redo();
		this.setIsModified(true, true);
	}

	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	public canSave(): boolean {
		return (
			this.isModified &&
			!this.isBeingSaved() &&
			this.abilitySvc.can(ABILITY_ACTIONS.WRITE, this.resource)
		);
	}

	private setSaveInProgress(saveInProgress: boolean): void {
		this.saveInProgress = saveInProgress;
		this.emitResourceInfoChange();
	}

	public async save(): Promise<boolean> {
		if (!this.resource) {
			throw new Error("Resource was not loaded.");
		}

		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.routesModel.exportDocumentAsYAML();

		this.setSaveInProgress(true);

		this.setIsModified(false, false);

		try {
			await this.resourceSvc.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);
			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.setSaveInProgress(false);
		}

		return true;
	}

	public getIsModified(): boolean {
		return this.isModified;
	}

	public getResourceInfo(): IResourceInfo {
		return {
			resource: this.resource,
			parentList: this.resourceSvc.getResourceParentList(this.resource.id),
			isBeingSaved: this.saveInProgress
		};
	}

	public getLayout(): LayoutState {
		return this.layout;
	}

	public getRoutesModel(): RoutesModel {
		return this.routesModel;
	}

	public getViewInfo(): IViewInfo {
		return {
			label: "Routes",
			icon: "mdi/routes",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true
		};
	}

	public dispose() {
		if (this.routesModel) {
			this.routesModel.dispose();
		}

		this.routesModel = null;

		removeAllEventListeners(this.onResourceInfoChanged);
		removeAllEventListeners(this.onViewInfoChanged);
	}
}
